import { Dispatch, memo, PropsWithChildren, SetStateAction, useCallback, useLayoutEffect, useState } from 'react'

import { styled, Typography, TypographyVariants } from '@mui/material'

import { FormStructure, TitleInput, TitleInputProps } from '@shared/components/ManagedTitle/TitleInput'
import { HotkeyItem, useHotkeys } from '@shared/utils/hooks'

type StaticTitleProps = {
  disabled?: boolean
}

const StaticTitle = styled(Typography, { shouldForwardProp: (prop) => prop !== 'disabled' })<StaticTitleProps>(
  ({ theme, disabled }) => ({
    padding: theme.spacing(0.2, 0.5),

    ...(!disabled && {
      '&:hover': {
        cursor: 'text',
        backgroundColor: theme.palette.background.levels[100],
        borderRadius: 1,
      },
    }),
  }),
)

export type AllowedVariants = keyof Pick<
  TypographyVariants,
  'heading-xl' | 'heading-lg' | 'heading-md' | 'heading-base'
>

type ManagedTitleProps = {
  title: string
  placeholder?: string
  variant?: AllowedVariants
  onManagedStateChange?: (state: boolean) => void
  hotKeyItems?: (onStateChange: Dispatch<SetStateAction<boolean>>) => HotkeyItem[]
}

export type ManagedTitleConditionalProps =
  | {
      disabled: true
      onSave?: never
    }
  | {
      disabled?: never
      onSave: (title: string, closeCallback: () => void) => void
    }

const ManagedTitleRaw = ({
  title,
  placeholder,
  disabled,
  onSave,
  variant = 'heading-xl',
  onManagedStateChange,
  clickAwayBehaviour,
  isLoading,
  hotKeyItems,
  children,
}: PropsWithChildren<
  ManagedTitleProps & Pick<TitleInputProps, 'clickAwayBehaviour' | 'isLoading'> & ManagedTitleConditionalProps
>) => {
  const [isManaging, setIsManaging] = useState(false)

  const handleSave = useCallback(
    (data: FormStructure) => {
      if (!disabled) {
        onSave(data.title, () => setIsManaging(false))
      }
    },
    [disabled, onSave],
  )

  useLayoutEffect(() => {
    onManagedStateChange?.(isManaging)
  }, [isManaging, onManagedStateChange])

  useHotkeys(hotKeyItems?.(setIsManaging) || [])

  if (!disabled && isManaging) {
    return (
      <TitleInput
        title={title}
        variant={variant}
        onSave={handleSave}
        onCancel={() => setIsManaging(false)}
        clickAwayBehaviour={clickAwayBehaviour}
        isLoading={isLoading}
      >
        {children}
      </TitleInput>
    )
  }

  return (
    <StaticTitle variant={variant} onClick={() => !disabled && setIsManaging(true)} noWrap disabled={disabled}>
      {title.length ? title : placeholder}
    </StaticTitle>
  )
}

export const ManagedTitle = memo(ManagedTitleRaw) as typeof ManagedTitleRaw
